import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppDispatch, useAppSelector} from '@hooks';
import {
  Resource,
  getBuildingResources,
  loadBuildingResources,
  transformResourcesToResourcesGroups,
} from '@lib/store/src/data/buildingResources';
import uniqBy from 'lodash/uniqBy';
import {trackEvent} from '@lib/infrastructure';
import {
  constructHereAndNowExploreFilter,
  useFilterData,
  SECTION_ID_FLOORS,
  resourceMatchesQuery,
  SECTION_ID_RESOURCE_TYPE_FILTER,
} from '@lib/logic';
import {getFloorsByBuildingId} from '@lib/store';

/*
  The setup of our Here & Now page does not really allow for elegant solutions in managing
  each tab's state...

  This hook is a consession; it makes it easier to pass state along between the list and map
  that are connected through the HereAndNow parent component.
*/
export function useExploreState(buildingId: string, initialQuery: string, preselectedResourceId: string | null) {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();

  // Ensure we have a list of all resources for building ready:
  const resourceData = useAppSelector((state) => getBuildingResources(state, buildingId));

  const floorIds = useMemo(() => new Set(resourceData?.floordIds ?? []), [resourceData?.floordIds]);
  const allResources = useMemo(() => resourceData?.resources ?? [], [resourceData?.resources]);
  const resourceGroups = useMemo(() => transformResourcesToResourcesGroups(t, allResources), [allResources, t]);

  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [spotlightedResource, setSpotlightedResource] = useState<Resource | null>(null);

  const loadResources = useCallback(() => {
    if (buildingId) {
      dispatch(loadBuildingResources(buildingId));
    }
  }, [dispatch, buildingId]);

  useEffect(
    function initialLoad() {
      if (!resourceData) loadResources();
    },
    [loadResources, resourceData],
  );

  // Set up filters
  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const floorsWithResources = useMemo(() => {
    return floors.filter((f) => floorIds.has(f.id));
  }, [floors, floorIds]);

  const exploreFilters = useMemo(
    () => constructHereAndNowExploreFilter(floorsWithResources, t),
    [floorsWithResources, t],
  );
  const {filteredData: filteredResources, selectedFilters} = useFilterData(
    'HereAndNow_Explore',
    buildingId,
    allResources,
    exploreFilters,
    'and',
    false,
  );

  const isFloorFilterSelected = !!selectedFilters[SECTION_ID_FLOORS]?.length;
  const hasFilterSelected = Object.values(selectedFilters).some((filters) => !!filters?.length);

  // Set up search logic
  const [rawQuery, setRawQuery] = useState(initialQuery);

  const [query, setQuery] = useState('');
  const hasSearchQuery = !!query;

  // for explore we either want to have a highlighted state for the filtered resources
  // or the resources that match the query
  const highlightedResources = useMemo(() => {
    return filteredResources.filter((r) => resourceMatchesQuery(r, query));
  }, [query, filteredResources]);

  const buildingIdOfSelectedResource = selectedResource?.buildingId;
  useEffect(
    function ensureSelectionIsInBuilding() {
      if (buildingIdOfSelectedResource !== buildingId) {
        setSelectedResource(null);
      }
    },
    [buildingIdOfSelectedResource, buildingId],
  );

  // Set up floor id behavior
  const [floorId, setFloorId] = useState<string | null>(null);
  useEffect(
    function autoSelectFloor() {
      // Selections always dictate floor
      if (selectedResource) {
        setFloorId(selectedResource.floorId);
      } else {
        // When there is no selection, the first search/filter/fetched result sets the floor id...
        const candidate = isFloorFilterSelected
          ? filteredResources.at(0)?.floorId ?? null
          : highlightedResources.at(0)?.floorId || allResources.at(0)?.floorId || null;

        setFloorId((prev) => {
          // ...but _only_ if it's not currently set
          if (!prev || !floorIds.has(prev) || isFloorFilterSelected) return candidate;

          return prev;
        });
      }
    },
    [selectedResource, highlightedResources, filteredResources, allResources, floorIds, isFloorFilterSelected],
  );

  useEffect(
    function clearSelectionIfNotInFilters() {
      const floorIdOfCurrentSelection = selectedResource?.floorId;
      if (floorIdOfCurrentSelection && !filteredResources.some((r) => r.floorId === floorIdOfCurrentSelection)) {
        setSelectedResource(null);
      }
    },
    [filteredResources, selectedResource?.floorId],
  );

  useEffect(() => {
    if (!query?.length) return;

    trackEvent('HereAndNow_Explore__Search', {
      buildingId,
      query,
      resultCount: highlightedResources.length,
      unfilteredCount: filteredResources.length,
    });
  }, [query, highlightedResources, dispatch, buildingId, filteredResources]);

  useEffect(() => {
    if (!preselectedResourceId?.length || resourceData?.status !== 'loaded') return;

    const resource = allResources.find((r) => r.id === preselectedResourceId);

    setSelectedResource(resource ?? null);
  }, [allResources, preselectedResourceId, resourceData?.status]);

  return {
    buildingId,
    floorId,
    loadResources,

    resourceData,
    allResources,
    resourceGroups,
    filteredResources,
    highlightedResources,

    selectedResource,
    setSelectedResource,

    spotlightedResource,
    setSpotlightedResource,

    activeTypeFilters: selectedFilters[SECTION_ID_RESOURCE_TYPE_FILTER] ?? [],

    query,
    setQuery,
    rawQuery,
    setRawQuery,

    hasSearchQuery,
    hasFilterSelected,
  };
}

export type ExploreState = ReturnType<typeof useExploreState>;
